import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { aiApi } from '../../lib/api';
import { saveImageToCharacter } from '../../lib/imageStorage';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import {
    Wand2, Send, Loader2, Image, Sparkles, PenLine, Lightbulb, 
    BookOpen, ChevronDown, ChevronUp, Save, Download
} from 'lucide-react';

const WritingAIAssistant = ({ 
    bookId, 
    chapterTitle, 
    currentContent, 
    onInsertText,
    characters = []
}) => {
    const { user, refreshUser } = useAuth();
    const [expanded, setExpanded] = useState(false);
    const [activeSection, setActiveSection] = useState('writing'); // 'writing' or 'illustration'
    
    // Writing assistance state
    const [writingPrompt, setWritingPrompt] = useState('');
    const [writingLoading, setWritingLoading] = useState(false);
    const [writingSuggestion, setWritingSuggestion] = useState('');
    const [selectedAssistType, setSelectedAssistType] = useState('continue');
    
    // Illustration state
    const [illustrationPrompt, setIllustrationPrompt] = useState('');
    const [illustrationPosition, setIllustrationPosition] = useState('');
    const [illustrationTier, setIllustrationTier] = useState('standard');
    const [illustrationLoading, setIllustrationLoading] = useState(false);
    const [generatedImage, setGeneratedImage] = useState(null);
    const [selectedCharacter, setSelectedCharacter] = useState('');

    const writingPrompts = [
        { id: 'continue', label: 'Continue Writing', prompt: 'Continue writing from where I left off, maintaining the same tone and style.' },
        { id: 'dialogue', label: 'Write Dialogue', prompt: 'Write a dialogue exchange between characters in this scene.' },
        { id: 'describe', label: 'Describe Scene', prompt: 'Describe the current scene in vivid sensory detail.' },
        { id: 'tension', label: 'Add Tension', prompt: 'Add tension or conflict to the current scene.' },
        { id: 'emotion', label: 'Deepen Emotion', prompt: 'Deepen the emotional impact of this moment.' },
        { id: 'transition', label: 'Write Transition', prompt: 'Write a smooth transition to the next scene or chapter.' },
    ];

    const handleWritingAssist = async () => {
        if (!writingPrompt.trim() && !selectedAssistType) return;
        
        setWritingLoading(true);
        setWritingSuggestion('');
        
        const selectedPrompt = writingPrompts.find(p => p.id === selectedAssistType);
        const fullPrompt = writingPrompt.trim() 
            ? writingPrompt 
            : selectedPrompt?.prompt || 'Help me continue writing.';
        
        try {
            const res = await aiApi.write({
                prompt: fullPrompt,
                context: `Chapter: ${chapterTitle}\n\nCurrent content (last 1000 chars): ${currentContent?.slice(-1000) || ''}`,
                assist_type: 'writing_assist'
            });
            
            setWritingSuggestion(res.data.response || res.data.suggestion || '');
            refreshUser();
            toast.success(`Used ${res.data.credits_used || 1} credit(s)`);
        } catch (error) {
            if (error.response?.status === 402) {
                toast.error('Insufficient credits');
            } else {
                toast.error('AI request failed');
            }
        } finally {
            setWritingLoading(false);
        }
    };

    const handleGenerateIllustration = async () => {
        if (!illustrationPrompt.trim()) {
            toast.error('Please describe the illustration');
            return;
        }
        
        setIllustrationLoading(true);
        setGeneratedImage(null);
        
        const character = characters.find(c => c.id === selectedCharacter);
        let fullPrompt = illustrationPrompt;
        if (character) {
            fullPrompt = `${character.name}: ${illustrationPrompt}`;
        }
        fullPrompt += ', book illustration style, detailed';
        
        try {
            const res = await aiApi.illustrate({
                prompt: fullPrompt,
                tier: illustrationTier,
                book_id: bookId
            });
            
            setGeneratedImage({
                url: res.data.image_url,
                prompt: illustrationPrompt,
                position: illustrationPosition,
                chapter: chapterTitle,
                characterId: selectedCharacter,
                characterName: character?.name
            });
            
            refreshUser();
            toast.success('Illustration generated!');
        } catch (error) {
            if (error.response?.status === 402) {
                toast.error('Insufficient credits');
            } else {
                toast.error('Failed to generate illustration');
            }
        } finally {
            setIllustrationLoading(false);
        }
    };

    const handleSaveIllustration = (setAsDefault = false) => {
        if (!generatedImage || !selectedCharacter) {
            toast.error('Please select a character to save the image to');
            return;
        }
        
        saveImageToCharacter(bookId, selectedCharacter, generatedImage.characterName, {
            url: generatedImage.url,
            title: `${generatedImage.characterName} - ${chapterTitle}`,
            bookLocation: { chapter: chapterTitle, page: '' },
            position: generatedImage.position
        }, setAsDefault);
        
        toast.success(`Image saved to ${generatedImage.characterName}'s gallery`);
        setGeneratedImage(null);
        setIllustrationPrompt('');
        setIllustrationPosition('');
    };

    return (
        <div className="border-t border-border mt-8">
            {/* Toggle Header */}
            <button
                onClick={() => setExpanded(!expanded)}
                className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors"
            >
                <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-accent/10">
                        <Wand2 className="h-5 w-5 text-accent" />
                    </div>
                    <div className="text-left">
                        <div className="font-semibold">AI Writing Assistant</div>
                        <div className="text-sm text-muted-foreground">Get help with writing and create illustrations</div>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <span className="text-sm text-muted-foreground">{user?.credits || 0} credits</span>
                    {expanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                </div>
            </button>

            {/* Expanded Content */}
            {expanded && (
                <div className="p-4 pt-0">
                    {/* Section Tabs */}
                    <div className="flex gap-2 mb-4">
                        <Button
                            variant={activeSection === 'writing' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setActiveSection('writing')}
                        >
                            <PenLine className="h-4 w-4 mr-2" />
                            Writing Help
                        </Button>
                        <Button
                            variant={activeSection === 'illustration' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setActiveSection('illustration')}
                        >
                            <Image className="h-4 w-4 mr-2" />
                            Create Illustration
                        </Button>
                    </div>

                    {/* Writing Help Section */}
                    {activeSection === 'writing' && (
                        <div className="space-y-4">
                            {/* Quick Prompts */}
                            <div>
                                <Label className="text-xs mb-2 block">Quick Prompts</Label>
                                <div className="flex flex-wrap gap-2">
                                    {writingPrompts.map(prompt => (
                                        <button
                                            key={prompt.id}
                                            className={`px-3 py-1.5 rounded-full text-xs transition-colors ${
                                                selectedAssistType === prompt.id
                                                    ? 'bg-accent text-accent-foreground'
                                                    : 'bg-muted hover:bg-muted/80'
                                            }`}
                                            onClick={() => setSelectedAssistType(prompt.id)}
                                        >
                                            {prompt.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Custom Prompt */}
                            <div>
                                <Label className="text-xs mb-2 block">Or ask anything...</Label>
                                <div className="flex gap-2">
                                    <Textarea
                                        value={writingPrompt}
                                        onChange={(e) => setWritingPrompt(e.target.value)}
                                        placeholder="What would you like help with? e.g., 'Make this dialogue more natural' or 'Describe the castle in more detail'"
                                        rows={2}
                                        className="flex-1"
                                    />
                                    <Button
                                        onClick={handleWritingAssist}
                                        disabled={writingLoading}
                                        className="self-end"
                                    >
                                        {writingLoading ? (
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : (
                                            <Send className="h-4 w-4" />
                                        )}
                                    </Button>
                                </div>
                            </div>

                            {/* Writing Suggestion */}
                            {writingSuggestion && (
                                <div className="p-4 bg-muted/50 rounded-lg">
                                    <div className="flex items-start gap-3 mb-3">
                                        <Sparkles className="h-5 w-5 text-accent mt-0.5" />
                                        <div className="flex-1">
                                            <div className="font-medium text-sm mb-2">AI Suggestion</div>
                                            <p className="text-sm whitespace-pre-wrap">{writingSuggestion}</p>
                                        </div>
                                    </div>
                                    <Button
                                        size="sm"
                                        onClick={() => {
                                            onInsertText(writingSuggestion);
                                            setWritingSuggestion('');
                                            toast.success('Text inserted into editor');
                                        }}
                                    >
                                        <PenLine className="h-3 w-3 mr-2" />
                                        Insert into Document
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Illustration Section */}
                    {activeSection === 'illustration' && (
                        <div className="grid md:grid-cols-2 gap-6">
                            {/* Form */}
                            <div className="space-y-4">
                                {characters.length > 0 && (
                                    <div>
                                        <Label className="text-xs mb-2 block">Character (optional)</Label>
                                        <select
                                            className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                                            value={selectedCharacter}
                                            onChange={(e) => setSelectedCharacter(e.target.value)}
                                        >
                                            <option value="">No specific character</option>
                                            {characters.map(char => (
                                                <option key={char.id} value={char.id}>{char.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                <div>
                                    <Label className="text-xs mb-2 block">Describe the illustration</Label>
                                    <Textarea
                                        value={illustrationPrompt}
                                        onChange={(e) => setIllustrationPrompt(e.target.value)}
                                        placeholder="Describe what you want to illustrate from this section..."
                                        rows={2}
                                    />
                                </div>

                                <div>
                                    <Label className="text-xs mb-2 block">Position in book (for reference)</Label>
                                    <Input
                                        value={illustrationPosition}
                                        onChange={(e) => setIllustrationPosition(e.target.value)}
                                        placeholder="Where should this go?"
                                    />
                                    <p className="text-xs text-muted-foreground mt-1">
                                        <strong>Examples:</strong> "Top right 1/3 of page", "Bottom 2" high x 6" wide", 
                                        "Full page", "80% transparent background to page"
                                    </p>
                                </div>

                                <div>
                                    <Label className="text-xs mb-2 block">Quality</Label>
                                    <div className="grid grid-cols-2 gap-2">
                                        <button
                                            className={`p-2 rounded-lg border text-xs text-left ${
                                                illustrationTier === 'standard' ? 'border-accent bg-accent/10' : 'border-border'
                                            }`}
                                            onClick={() => setIllustrationTier('standard')}
                                        >
                                            <div className="font-medium">Standard</div>
                                            <div className="text-muted-foreground">5 credits</div>
                                        </button>
                                        <button
                                            className={`p-2 rounded-lg border text-xs text-left ${
                                                illustrationTier === 'premium' ? 'border-accent bg-accent/10' : 'border-border'
                                            }`}
                                            onClick={() => setIllustrationTier('premium')}
                                        >
                                            <div className="font-medium">Premium</div>
                                            <div className="text-muted-foreground">10 credits</div>
                                        </button>
                                    </div>
                                </div>

                                <Button
                                    onClick={handleGenerateIllustration}
                                    disabled={illustrationLoading || !illustrationPrompt.trim()}
                                    className="w-full"
                                >
                                    {illustrationLoading ? (
                                        <>
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                            Generating...
                                        </>
                                    ) : (
                                        <>
                                            <Image className="h-4 w-4 mr-2" />
                                            Generate Illustration
                                        </>
                                    )}
                                </Button>
                            </div>

                            {/* Preview */}
                            <div>
                                {generatedImage ? (
                                    <div className="space-y-3">
                                        <div className="aspect-square rounded-lg overflow-hidden bg-muted">
                                            <img 
                                                src={generatedImage.url} 
                                                alt="Generated illustration"
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                        <div className="p-2 bg-muted/50 rounded text-xs">
                                            <p><strong>Chapter:</strong> {generatedImage.chapter}</p>
                                            {generatedImage.position && (
                                                <p><strong>Position:</strong> {generatedImage.position}</p>
                                            )}
                                        </div>
                                        <div className="flex gap-2">
                                            {selectedCharacter ? (
                                                <Button size="sm" onClick={() => handleSaveIllustration(false)} className="flex-1">
                                                    <Save className="h-3 w-3 mr-1" />
                                                    Save to Character
                                                </Button>
                                            ) : (
                                                <p className="text-xs text-muted-foreground flex-1">
                                                    Select a character above to save this image
                                                </p>
                                            )}
                                            <Button size="sm" variant="outline" asChild>
                                                <a href={generatedImage.url} download="illustration.png" target="_blank" rel="noopener noreferrer">
                                                    <Download className="h-3 w-3" />
                                                </a>
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="aspect-square rounded-lg bg-muted/50 flex items-center justify-center">
                                        <div className="text-center">
                                            <Image className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
                                            <p className="text-sm text-muted-foreground">
                                                Generated illustration will appear here
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default WritingAIAssistant;
